import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Chip, FormControlLabel, IconButton, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { ConfirmDialog, EditDialog } from './EditDialog';
import FilterEditor from './FilterEditor';
import { useEditor } from './useEditor';
import { builderUrl, emptyRuleSet, filterText, labelChoices, machinesMatching, ruleSetBody, ruleSetProblem } from './policyEdit';

const list = (v) => (Array.isArray(v) ? v : []);

// The rule sets of the account, which work like endpoint schedules: a filter (label is value, all must match) picks the machines by their
// labels in VES. A machine gets the rules of every rule set whose filter matches; where two have a rule for the same program the one with
// more filters wins. "account is myorg" is the whole account. A rule set is made here (its filter cannot be changed afterwards: the filter
// is what identifies it) and its software is chosen on the builder page, which Edit opens.
function RulesTab({ account, data, devices, canEdit, onSave, onDelete }) {
  const navigate = useNavigate();
  // A new rule set is saved empty and the person goes straight on to choose its software.
  const editor = useEditor({
    onSave: async (body) => {
      const saved = await onSave(body);
      navigate(builderUrl(account, saved?.id));
    },
    onDelete,
  });
  const machines = list(devices?.devices);
  const machinesKnown = devices?.state === 'ok' && devices.devicesAvailable !== false;
  const ruleSets = [...list(data?.ruleSets)].sort((a, b) => list(a.filter).length - list(b.filter).length || filterText(a.filter).localeCompare(filterText(b.filter)));
  const form = editor.editing?.form;
  const preview = form ? machinesMatching(ruleSetBody(account, form).filter, machines) : [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Like an endpoint schedule: a filter picks the machines by their labels in VES, and the rule set says which programs they must have, may have, or must not have.
          Where two rule sets have a rule for the same program, the one with more filters wins. The filter account is {account || 'the account'} is the whole account.
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" disabled={!canEdit} onClick={() => editor.open(emptyRuleSet(account))} sx={{ flexShrink: 0 }}>Add rule set</Button>
      </Stack>

      {ruleSets.length === 0 ? (
        <Typography variant="body2" color="text.secondary">This account has no rule sets yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Rule sets">
            <TableHead>
              <TableRow>{['Status', 'Filter', 'Description', 'Machines', 'Last changed', ''].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {ruleSets.map((rs) => {
                const matched = machinesMatching(rs.filter, machines);
                const name = filterText(rs.filter);
                return (
                  <TableRow key={rs.id || name} hover sx={{ verticalAlign: 'top', opacity: rs.enabled === false ? 0.55 : 1 }}>
                    <TableCell><Chip size="small" color={rs.enabled === false ? 'default' : 'success'} variant="outlined" label={rs.enabled === false ? 'Off' : 'Active'} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {list(rs.filter).map((f) => <Chip key={`${f.label}-${f.value}`} size="small" variant="outlined" label={`${f.label} is ${f.value}`} />)}
                      </Stack>
                    </TableCell>
                    <TableCell>{rs.description}</TableCell>
                    <TableCell title={matched.map((m) => m.hostname).join(', ')}>{machinesKnown ? matched.length : '-'}</TableCell>
                    <TableCell><Typography variant="caption">{[rs.updatedBy, rs.updatedUtc && rs.updatedUtc.slice(0, 10)].filter(Boolean).join(' ')}</Typography></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Choose the software of this rule set"><span><IconButton size="small" component={Link} to={builderUrl(account, rs.id)} aria-label={`Edit rule set ${name}`} disabled={!canEdit}><EditIcon fontSize="inherit" /></IconButton></span></Tooltip>
                      <Tooltip title="Delete"><span><IconButton size="small" aria-label={`Delete rule set ${name}`} disabled={!canEdit}
                        onClick={() => editor.askDelete(rs.id, `Delete the rule set for ${name}${rs.description ? ` (${rs.description})` : ''} and its ${list(rs.rules).length} rule${list(rs.rules).length === 1 ? '' : 's'}? The machines it matched get the rules of the other rule sets. The change is kept in the history.`)}><DeleteOutlineIcon fontSize="inherit" /></IconButton></span></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EditDialog
        open={Boolean(form)} title="Add rule set" onClose={editor.close} saving={editor.saving} error={editor.error} saveLabel="Save and choose software"
        problem={form ? ruleSetProblem({ ...form, rules: [] }, []) : ''} onSave={() => editor.save((f) => ruleSetBody(account, { ...f, rules: [] }))}
      >
        {form && (
          <Stack spacing={2}>
            <TextField label="Description" value={form.description} onChange={(e) => editor.change({ description: e.target.value })} inputProps={{ maxLength: 200 }} fullWidth
              helperText="For you: for example Developers. The filter is what identifies the rule set, and it cannot be changed afterwards." />
            <FilterEditor filter={form.filter} choices={labelChoices(machines)} onChange={(filter) => editor.replace({ ...form, filter })} />
            {machinesKnown ? (
              <Alert severity={preview.length ? 'info' : 'warning'} variant="outlined" aria-label="Machines this matches">
                {preview.length
                  ? `Matches ${preview.length} machine${preview.length === 1 ? '' : 's'} now: ${preview.map((m) => m.hostname).join(', ')}.`
                  : 'No machine has these labels at the moment.'}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">The machine list could not be read from VES, so what this matches cannot be shown.</Typography>
            )}
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => editor.change({ enabled: e.target.checked })} />} label="Rule set is on" />
          </Stack>
        )}
      </EditDialog>

      <ConfirmDialog open={Boolean(editor.deleting)} title="Delete rule set" text={editor.deleting?.text} busy={editor.busy} error={editor.deleteError}
        onCancel={editor.cancelDelete} onConfirm={editor.confirmDelete} />
    </Stack>
  );
}

export default RulesTab;
